import React, { useEffect, useState } from 'react';
import { AlertTriangle, Unlock, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Admin-only banner that surfaces orphan affirmation clips: documents
 * whose audio is irrecoverable (no audio_b64 stored AND no file on disk).
 * If they are also `locked`, re-uploads silently fail with 403 — which is
 * exactly what kept biting this user. The "Déverrouiller" button calls
 * /admin/affirmation-clips/unlock-orphans so the next upload overwrites
 * the broken doc cleanly.
 */
export default function OrphanClipsBanner({ onUnlock }) {
  const [stats, setStats] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const fetchStats = async () => {
    try {
      const r = await axios.get(`${API}/api/admin/affirmation-clips/orphans-stats`);
      setStats(r.data || null);
    } catch (e) {
      // Non-blocking; the banner just stays hidden if the endpoint is down.
      console.warn('[OrphanBanner] fetch stats failed', e);
    }
  };

  useEffect(() => { fetchStats(); }, []);

  const handleUnlock = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/api/affirmation-clips/unlock-orphans`);
      const u = r.data?.unlocked || 0;
      toast.success(`🔓 ${u} clip(s) déverrouillé(s). Vous pouvez maintenant réuploader.`);
      await fetchStats();
      onUnlock && onUnlock();
    } catch (e) {
      console.error('[OrphanBanner] unlock failed', e);
      toast.error('Déverrouillage échoué. Réessayez.');
    } finally {
      setBusy(false);
    }
  };

  if (dismissed || !stats) return null;
  const total = (stats.locked_orphans || 0) + (stats.unlocked_orphans || 0);
  if (total === 0) return null;

  return (
    <div
      className="mt-3 rounded-2xl border border-amber-400/40 bg-amber-500/10 p-3 sm:p-4 text-amber-100"
      data-testid="orphan-clips-banner"
    >
      <div className="flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-amber-300 flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-amber-200">
            {total} clip{total > 1 ? 's' : ''} audio à réuploader
          </p>
          <p className="text-xs text-amber-100/80 mt-1 leading-relaxed">
            {stats.locked_orphans > 0 ? (
              <>
                <strong>{stats.locked_orphans}</strong> clip{stats.locked_orphans > 1 ? 's sont verrouillés' : ' est verrouillé'} mais leur audio a été perdu (ancien stockage disque). Cliquez ci-dessous pour les déverrouiller — vos prochains uploads les écraseront proprement.
              </>
            ) : (
              <>{stats.unlocked_orphans} clip(s) sans audio détectés. Réuploadez-les pour restaurer la voix.</>
            )}
          </p>
          {stats.orphans_by_theme && Object.keys(stats.orphans_by_theme).length > 0 && (
            <div className="text-[10px] text-amber-100/60 mt-1.5">
              Par thème :{' '}
              {Object.entries(stats.orphans_by_theme)
                .map(([t, n]) => `${t}=${n}`)
                .join(' · ')}
            </div>
          )}
          {stats.locked_orphans > 0 && (
            <button
              onClick={handleUnlock}
              disabled={busy}
              data-testid="orphan-clips-unlock-btn"
              className="mt-2.5 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-400/20 hover:bg-amber-400/30 border border-amber-300/50 text-amber-100 text-xs font-semibold disabled:opacity-50 disabled:cursor-wait transition"
            >
              {busy ? (
                <>
                  <Loader2 size={12} className="animate-spin" />
                  Déverrouillage…
                </>
              ) : (
                <>
                  <Unlock size={12} />
                  Déverrouiller automatiquement
                </>
              )}
            </button>
          )}
        </div>
        <button
          onClick={() => setDismissed(true)}
          className="text-amber-200/60 hover:text-amber-100 flex-shrink-0"
          aria-label="Fermer"
          data-testid="orphan-clips-banner-close"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
}
